import { useState } from 'react';

import {
  type AtlasGqlInspectionType,
  type AtlasGqlMetadataFieldMapping,
  AtlasGqlFileFormat,
  AtlasGqlInspectionStatus,
} from 'types/atlas-graphql';

import { type Steps } from '../UploadInspectionDataModal';

import {
  type ErroredUploadSteps,
  type UploadInspectionDataModalContextErrorState,
  UploadInspectionDataModalContext,
} from './UploadInspectionDataModalContext';

interface UploadInspectionDataModalContextProviderProps {
  children: React.ReactNode;
}

export function UploadInspectionDataModalContextProvider({
  children,
}: UploadInspectionDataModalContextProviderProps) {
  const [alertState, setAlertState] = useState<UploadInspectionDataModalContextErrorState | null>(
    null
  );
  const [useFolderMethod, setUseFolderMethod] = useState<boolean>(false);
  const [pendingStep, setPendingStep] = useState<Steps | undefined>();
  const [erroredUploadSteps, setErroredUploadSteps] = useState<ErroredUploadSteps>({
    summary: false,
    imageDetails: false,
  });
  const [file, setFile] = useState<File | undefined>();
  const [fieldMappings, setFieldMappings] = useState<AtlasGqlMetadataFieldMapping[] | undefined>();
  const [providedFieldNames, setProvidedFieldNames] = useState<string[]>([]);
  const [imagePathFieldName, setImagePathFieldName] = useState<string>('');
  const [previewData, setPreviewData] = useState<
    {
      [key: string]: string | number;
    }[]
  >([]);
  const [imageDetailsFileType, setImageDetailsFileType] = useState<AtlasGqlFileFormat>(
    AtlasGqlFileFormat.Csv
  );
  const [someImagesMatchingMetadata, setSomeImagesMatchingMetadata] = useState<boolean>(true);
  const [inspectionType, setInspectionType] = useState<AtlasGqlInspectionType>();
  const [createdInspectionIds, setCreatedInspectionIds] = useState<string[]>([]);
  const [createdTaskIds, setCreatedTaskIds] = useState<string[]>([]);
  const [shouldAutoRelease, setShouldAutoRelease] = useState<boolean>(true);
  const [autoReleaseStatus, setAutoReleaseStatus] = useState<AtlasGqlInspectionStatus>(
    AtlasGqlInspectionStatus.AnalysisPending
  );

  const resetState = () => {
    setUseFolderMethod(false);
    setAlertState(null);
    setErroredUploadSteps({ summary: false, imageDetails: false });
    setPendingStep(undefined);
    setFile(undefined);
    setShouldAutoRelease(true);
    setAutoReleaseStatus(AtlasGqlInspectionStatus.AnalysisPending);
  };

  const contextValue = {
    alertState,
    setAlertState,
    shouldAutoRelease,
    setShouldAutoRelease,
    autoReleaseStatus,
    setAutoReleaseStatus,
    useFolderMethod,
    setUseFolderMethod,
    pendingStep,
    setPendingStep,
    erroredUploadSteps,
    setErroredUploadSteps,
    inspectionType,
    setInspectionType,
    createdInspectionIds,
    setCreatedInspectionIds,
    createdTaskIds,
    setCreatedTaskIds,
    imageDetailsContext: {
      file,
      setFile,
      fieldMappings,
      setFieldMappings,
      providedFieldNames,
      setProvidedFieldNames,
      imagePathFieldName,
      setImagePathFieldName,
      previewData,
      setPreviewData,
      imageDetailsFileType,
      setImageDetailsFileType,
      someImagesMatchingMetadata,
      setSomeImagesMatchingMetadata,
    },
    resetState,
  };

  return (
    <UploadInspectionDataModalContext.Provider value={contextValue}>
      {children}
    </UploadInspectionDataModalContext.Provider>
  );
}
